import React, { useState } from "react";
import Alert from "react-bootstrap/Alert";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Spinner from "react-bootstrap/Spinner";
import Table from "react-bootstrap/Table";
import Tooltip from "react-bootstrap/Tooltip";

import {
  deleteWhatsAppTemplate,
  provisionWhatsAppTemplates,
  syncWhatsAppTemplates,
} from "../../whatsappApi";

/**
 * WhatsAppTemplateList: the practice's approved message templates.
 *
 * Visual conventions: /CLAUDE.md §1.6 (desktop table paired with a mobile
 * .acc-card list), §1.9 (badges), §1.4 (icon-only row actions with tooltips,
 * not a kebab, because §1.10 rules those out inside a table-responsive).
 */

const STATUS_VARIANT = {
  APPROVED: "success",
  PENDING: "secondary",
  REJECTED: "danger",
  PAUSED: "warning",
  DISABLED: "secondary",
};

/**
 * Where each template is sent from.
 *
 * This is the distinction that matters and it was invisible here: the inbox
 * composer can supply a first name and the practice name and nothing else, so
 * the appointment templates, which want a date and a time, are sent only by
 * the reminder job, off the appointment. Offering one in the inbox produced a
 * button that always failed on the parameter count.
 *
 * Meta's own sample templates (`hello_world` and the `sample_*` set) never
 * reach this list. The service filters them out, because they are Meta's demo
 * content and mean nothing to a practice.
 *
 * The flags are computed server-side (whatsappTemplateService) and the send
 * route enforces them, so this column states the rule rather than describing a
 * separate guess at it.
 */
function originOf(template) {
  if (template.manualSend) {
    return {
      label: "Inbox",
      variant: "primary",
      hint: "Staff can send this by hand from the WhatsApp inbox.",
    };
  }
  if (template.standard) {
    return {
      label: "Reminders",
      variant: "secondary",
      hint: "Sent automatically with an appointment's date and time. Map it to an interval below.",
    };
  }
  // Not ours and not Meta's, so the practice wrote it, which is exactly what
  // the reminder mapping offers as an alternative to the standard set.
  return {
    label: "Custom",
    variant: "light",
    hint: "Your own template. Map it to a reminder interval below to use it.",
  };
}

function OriginBadge({ template }) {
  const origin = originOf(template);
  return (
    <OverlayTrigger placement="top" overlay={<Tooltip>{origin.hint}</Tooltip>}>
      <Badge
        bg={origin.variant}
        text={origin.variant === "light" ? "dark" : undefined}
      >
        {origin.label}
      </Badge>
    </OverlayTrigger>
  );
}

function StatusBadge({ template }) {
  const badge = (
    <Badge bg={STATUS_VARIANT[template.status] || "secondary"}>
      {template.status || "UNKNOWN"}
    </Badge>
  );

  if (template.status === "REJECTED" && template.rejectedReason) {
    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip>{template.rejectedReason}</Tooltip>}
      >
        {badge}
      </OverlayTrigger>
    );
  }
  return badge;
}

export default function WhatsAppTemplateList({
  practiceNumber,
  templates,
  standardSet,
  canConfigure,
  onChanged,
  showToast,
}) {
  const [busy, setBusy] = useState(null);
  const [justProvisioned, setJustProvisioned] = useState(false);

  // What is missing comes from the server's own definition of the standard
  // set. The old check counted approved `practosync_` templates against a
  // hard-coded 4, which went stale the moment the set changed, and disabled
  // the button while a template sat PENDING with nothing explaining why.
  const missing = standardSet?.missing || [];
  const nothingToProvision = !!standardSet && missing.length === 0;

  const run = async (key, fn, successMessage) => {
    setBusy(key);
    try {
      await fn();
      if (successMessage) showToast?.("success", successMessage);
      onChanged?.();
    } catch (err) {
      showToast?.(
        "danger",
        err?.response?.data?.error || "The request could not be completed.",
      );
    } finally {
      setBusy(null);
    }
  };

  const provision = () =>
    run(
      "provision",
      async () => {
        await provisionWhatsAppTemplates(practiceNumber, ["en"]);
        setJustProvisioned(true);
      },
      "Templates submitted to Meta for approval.",
    );

  const sync = () =>
    run("sync", () => syncWhatsAppTemplates(practiceNumber));

  const remove = (name) =>
    run("delete", () => deleteWhatsAppTemplate(practiceNumber, name), "Template deleted.");

  return (
    <div className="acc-list mb-3">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <h6 className="mb-0 text-primary fw-bold">Message templates</h6>
        {canConfigure && (
          <div className="ms-auto d-flex gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={provision}
              disabled={busy !== null || nothingToProvision}
            >
              {busy === "provision" ? (
                <Spinner size="sm" animation="border" />
              ) : (
                "Provision standard templates"
              )}
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={sync}
              disabled={busy !== null}
            >
              {busy === "sync" ? (
                <Spinner size="sm" animation="border" />
              ) : (
                <>
                  <i className="fa-solid fa-rotate me-2" />
                  Refresh status
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Provisioning submits to Meta and is idempotent, so without this the
          button is either greyed out for no stated reason or appears to do
          nothing because every template was already there. */}
      {standardSet && (
        <p className="mb-2">
          <small className="text-muted">
            {nothingToProvision
              ? `All ${standardSet.total} standard templates are in this practice's WhatsApp account. Templates you add yourself in Meta Business Manager appear here too.`
              : `${standardSet.present} of ${standardSet.total} standard templates are in this account. Missing: ${missing
                  .map((m) => m.name)
                  .join(", ")}.`}
          </small>
        </p>
      )}

      {justProvisioned && (
        <Alert variant="info" className="py-2 px-3">
          Templates usually take a few minutes to be approved. You can leave this
          page. The status updates automatically.
        </Alert>
      )}

      {!templates.length ? (
        <p className="mb-0">
          No templates yet. Provision the standard templates to get started.
        </p>
      ) : (
        <>
          <div className="d-none d-lg-block">
            <Table bordered hover responsive>
              <thead className="text-center">
                <tr>
                  <th className="text-center align-middle text-primary">Template</th>
                  <th className="text-center align-middle text-primary">Language</th>
                  <th className="text-center align-middle text-primary">Sent from</th>
                  <th className="text-center align-middle text-primary">Status</th>
                  <th className="text-center align-middle text-primary">Message</th>
                  {canConfigure && (
                    <th className="text-center align-middle text-primary">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => (
                  <tr key={`${t.name}-${t.language}`}>
                    <td>{t.name}</td>
                    <td className="text-center">{t.language}</td>
                    <td className="text-center">
                      <OriginBadge template={t} />
                    </td>
                    <td className="text-center">
                      <StatusBadge template={t} />
                    </td>
                    <td>
                      <small>{t.body}</small>
                    </td>
                    {canConfigure && (
                      <td className="text-center">
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Delete template</Tooltip>}
                        >
                          <Button
                            variant="link"
                            className="text-primary text-decoration-none p-0"
                            style={{ width: 24, height: 24 }}
                            onClick={() => remove(t.name)}
                            disabled={busy !== null}
                          >
                            <i className="fa-solid fa-trash" />
                          </Button>
                        </OverlayTrigger>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <div className="d-lg-none">
            {templates.map((t) => (
              <div className="acc-card" key={`${t.name}-${t.language}`}>
                <div className="acc-card-top">
                  <span className="acc-card-id">{t.name}</span>
                  <StatusBadge template={t} />
                </div>
                <div className="acc-card-body">
                  <div className="acc-card-field">
                    <span className="acc-card-label">Language</span>
                    <span className="acc-card-value">{t.language}</span>
                  </div>
                  <div className="acc-card-field">
                    <span className="acc-card-label">Sent from</span>
                    <span className="acc-card-value">
                      <OriginBadge template={t} />
                    </span>
                  </div>
                  <div className="acc-card-field">
                    <span className="acc-card-label">Message</span>
                    <span className="acc-card-value">{t.body}</span>
                  </div>
                  {canConfigure && (
                    <div className="acc-card-field">
                      <span className="acc-card-label">Actions</span>
                      <span className="acc-card-value">
                        <Button
                          variant="link"
                          className="text-primary text-decoration-none p-0"
                          onClick={() => remove(t.name)}
                          disabled={busy !== null}
                        >
                          <i className="fa-solid fa-trash me-2" />
                          Delete
                        </Button>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
